import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Label,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { hedgesCompanyApi, companyApi, type Hedge } from "@/services/api";
import {
  TrendingUp,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  DollarSign,
  ArrowRight,
  Info,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";

function formatUsdc(value: string | number | undefined): string {
  if (!value) return "$0.00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatBrl(value: string | number | undefined): string {
  if (!value) return "R$ 0,00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(num);
}

export function LiquidarHedgePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hedge, setHedge] = useState<Hedge | null>(null);
  const [loading, setLoading] = useState(true);
  const [settleLoading, setSettleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<{ usdc: number; brld: number }>({ usdc: 0, brld: 0 });
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [hedgeData, dashboardData] = await Promise.all([
        hedgesCompanyApi.getHedge(id),
        companyApi.dashboard.get(),
      ]);
      setHedge(hedgeData);

      const brldBalance = dashboardData.balance?.availableBalance || 0;
      const usdcBalance = dashboardData.balance?.usdcBalance || 0;
      setBalance({ usdc: usdcBalance / 100, brld: brldBalance / 100 });
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      setError("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSettle = async () => {
    if (!hedge || !id) return;

    try {
      setSettleLoading(true);
      setError(null);

      await hedgesCompanyApi.settleHedge(id);

      setShowSuccessModal(true);
    } catch (err: unknown) {
      console.error("Erro ao liquidar hedge:", err);
      const errorMessage = err instanceof Error ? err.message : "Erro ao liquidar hedge";
      setError(errorMessage);
    } finally {
      setSettleLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hedge) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="w-12 h-12 text-muted-foreground" />
        <p className="text-muted-foreground">Hedge não encontrado</p>
        <Button onClick={() => navigate("/protecao-cambial")}>
          Voltar para Lista
        </Button>
      </div>
    );
  }

  if (hedge.status !== "ACTIVE" && hedge.status !== "PENDING_SETTLEMENT") {
    navigate(`/protecao-cambial/${id}`);
    return null;
  }

  // Cálculos
  const lockedRate = parseFloat(hedge.lockedExchangeRate || "0");
  const hedgeAmountUsdc = parseFloat(hedge.hedgeAmountUsdc || "0");
  const guaranteeUsdc = parseFloat(hedge.guaranteeAmountUsdc || "0");
  const lockedAmountBrl = parseFloat(hedge.lockedAmountBrl || "0");

  // Valor restante a pagar (80% se garantia é 20%)
  const remainingUsdc = hedgeAmountUsdc - guaranteeUsdc;
  const remainingBrl = remainingUsdc * lockedRate;

  // Comparativo com taxa atual (mockado)
  const currentRate = 5.50;
  const currentTotalBrl = hedgeAmountUsdc * currentRate;
  const savings = currentTotalBrl - lockedAmountBrl;
  const isSaving = savings > 0;

  // Verificar saldo BRLD
  const hasEnoughBalance = balance.brld >= remainingBrl;
  const missingBrl = remainingBrl - balance.brld;

  // Dias até vencimento
  const daysUntilDue = Math.ceil(
    (new Date(hedge.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  const isNearDue = daysUntilDue <= 7 && daysUntilDue > 0;
  const isOverdue = daysUntilDue < 0;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<TrendingUp className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Liquidar Hedge"
        description={`Hedge ${hedge.hedgeCode}`}
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => navigate(`/protecao-cambial/${id}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        }
      />

      {/* Alert de vencimento */}
      {(isNearDue || isOverdue) && (
        <Card className={cn(
          "border-l-4",
          isOverdue ? "border-l-red-500 bg-red-50" : "border-l-orange-500 bg-orange-50"
        )}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              {isOverdue ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <Clock className="h-5 w-5 text-orange-600" />
              )}
              <div>
                <p className={cn(
                  "font-medium",
                  isOverdue ? "text-red-700" : "text-orange-700"
                )}>
                  {isOverdue
                    ? `Hedge vencido há ${Math.abs(daysUntilDue)} dia${Math.abs(daysUntilDue) !== 1 ? "s" : ""}`
                    : `Vence em ${daysUntilDue} dia${daysUntilDue !== 1 ? "s" : ""}`
                  }
                </p>
                <p className={cn(
                  "text-sm",
                  isOverdue ? "text-red-600" : "text-orange-600"
                )}>
                  Liquide agora para garantir sua taxa travada.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Valor Final Garantido */}
      <Card className={cn(
        "border-2",
        isSaving ? "border-green-200 bg-green-50/50" : "border-orange-200 bg-orange-50/50"
      )}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className={cn("h-5 w-5", isSaving ? "text-green-600" : "text-orange-600")} />
            Valor Final Garantido
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="text-muted-foreground">Valor final garantido em BRL</Label>
              <p className="text-2xl font-bold text-green-600">
                {formatBrl(lockedAmountBrl)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Baseado em uma taxa de referência de R$ {lockedRate.toFixed(4)}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Valor sem proteção (taxa atual)</Label>
              <p className="text-xl font-bold">
                {formatBrl(currentTotalBrl)}
              </p>
              <p className="text-xs text-muted-foreground">* Taxa ilustrativa: R$ {currentRate.toFixed(4)}</p>
            </div>
            <div className={cn(
              "rounded-lg p-4",
              isSaving ? "bg-green-100" : "bg-orange-100"
            )}>
              <Label className={isSaving ? "text-green-800" : "text-orange-800"}>
                {isSaving ? "Você Está Economizando" : "Custo de Proteção"}
              </Label>
              <p className={cn(
                "text-2xl font-bold",
                isSaving ? "text-green-700" : "text-orange-700"
              )}>
                {formatBrl(Math.abs(savings))}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Valores para liquidação */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Valores para Liquidação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Valor final garantido em BRL:</span>
              <span className="text-xl font-bold text-primary">{formatBrl(lockedAmountBrl)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Valor total do hedge:</span>
              <span className="font-medium">{formatUsdc(hedgeAmountUsdc)}</span>
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Garantia depositada:</span>
                <span>{formatUsdc(guaranteeUsdc)} ({formatBrl(guaranteeUsdc * lockedRate)})</span>
              </div>
              <div className="flex justify-between bg-primary/10 p-3 rounded-lg">
                <span className="font-medium">Valor a converter:</span>
                <span className="font-bold">{formatBrl(remainingBrl)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Seu Saldo BRLD</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Disponível</Label>
              <p className="text-2xl font-bold">{formatBrl(balance.brld)}</p>
            </div>

            <div className="border-t pt-4">
              <Label className="text-muted-foreground">Necessário para liquidação</Label>
              <p>{formatBrl(remainingBrl)}</p>
            </div>

            {hasEnoughBalance ? (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Saldo suficiente</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Faltam {formatBrl(missingBrl)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p>
            Ao liquidar, o valor final garantido de <strong>{formatBrl(lockedAmountBrl)}</strong> será utilizado
            (baseado em uma taxa de referência de R$ {lockedRate.toFixed(4)}).
            Sua garantia de {formatUsdc(guaranteeUsdc)} será liberada automaticamente.
          </p>
        </div>
      </div>

      {/* Botões de ação */}
      <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
        <Button variant="outline" onClick={() => navigate(`/protecao-cambial/${id}`)}>
          Cancelar
        </Button>

        {hasEnoughBalance ? (
          <Button
            size="lg"
            onClick={handleSettle}
            disabled={settleLoading}
          >
            {settleLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <TrendingUp className="h-4 w-4 mr-2" />
            )}
            Liquidar Proteção
          </Button>
        ) : (
          <Button
            size="lg"
            onClick={() => navigate("/brld")}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Depositar BRLD
          </Button>
        )}
      </div>

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 overflow-hidden">
            <CardContent className="pt-8 pb-6 space-y-6">
              <div className="text-center space-y-3">
                <div className="relative mx-auto w-20 h-20">
                  <div className="absolute inset-0 rounded-full bg-green-500/20 animate-pulse" />
                  <div className="relative w-20 h-20 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-green-600">Hedge Liquidado!</h2>
                <p className="text-muted-foreground">
                  Sua proteção cambial foi liquidada com sucesso.
                </p>
              </div>

              <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Valor final pago em BRL</span>
                  <span className="font-bold text-primary">{formatBrl(lockedAmountBrl)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Valor liquidado</span>
                  <span className="font-medium">{formatUsdc(hedgeAmountUsdc)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Taxa de referência</span>
                  <span className="text-xs text-muted-foreground">R$ {lockedRate.toFixed(4)}</span>
                </div>
                {isSaving && (
                  <div className="flex justify-between text-green-600 bg-green-100 p-3 rounded-lg">
                    <span className="font-medium">Economia</span>
                    <span className="font-bold">{formatBrl(savings)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Garantia liberada</span>
                  <span className="font-medium">{formatUsdc(guaranteeUsdc)}</span>
                </div>
              </div>

              <Button className="w-full" onClick={() => navigate("/protecao-cambial")}>
                Voltar para Lista
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
